import { io, Socket } from 'socket.io-client';
import { API_URL } from './utils';

export type { Socket };

const getSocketUrl = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL || API_URL?.replace('/api/v1', '') || '';
  return WEBSOCKET_URL;
};

let socket: Socket | null = null;
let errorLogCount = 0;
let lastErrorLogTime = 0;
const ERROR_LOG_THROTTLE_MS = 5000;
const MAX_ERROR_LOGS = 3;

export const getSocket = (token?: string): Socket => {
  if (socket?.connected) {
    return socket;
  }

  const url = getSocketUrl();
  if (!url) {
    throw new Error('WebSocket URL not configured');
  }

  const authToken = token || getTokenFromStorage();

  socket = io(`${url}/notifications`, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: 5,
    withCredentials: true,
    auth: authToken
      ? {
          token: authToken,
        }
      : undefined,
    extraHeaders: authToken
      ? {
          Authorization: `Bearer ${authToken}`,
        }
      : {},
  });

  socket.on('connect', () => {
    console.log('WebSocket connected');
    errorLogCount = 0;
    lastErrorLogTime = 0;
  });

  socket.on('disconnect', (reason) => {
    if (import.meta.env.DEV || reason !== 'io client disconnect') {
      console.log('WebSocket disconnected:', reason);
    }
  });

  socket.on('connect_error', (error) => {
    const now = Date.now();
    const shouldLog =
      import.meta.env.DEV ||
      (errorLogCount < MAX_ERROR_LOGS && now - lastErrorLogTime > ERROR_LOG_THROTTLE_MS);

    if (shouldLog) {
      console.error('WebSocket connection error:', error);
      errorLogCount++;
      lastErrorLogTime = now;
    }
  });

  socket.on('reconnect_failed', () => {
    console.warn(
      'WebSocket: Maximum reconnection attempts reached. Connection will not be retried automatically.',
    );
  });

  return socket;
};

export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

const getTokenFromStorage = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const cookies = document.cookie.split(';');
    const accessTokenCookie = cookies.find((cookie) =>
      cookie.trim().startsWith('accessToken='),
    );
    if (accessTokenCookie) {
      return accessTokenCookie.split('=')[1]?.trim();
    }
  } catch (error) {
    console.error('Error reading token from storage:', error);
  }

  return undefined;
};


